import {
  ApolloClient, createHttpLink, InMemoryCache, ApolloLink, Observable,
} from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';

import * as Cognito from './api/cognito';
import { getSuperAdminData } from './helpers/super-admin';
import { isAbortError } from '@/utils/abort-error';
import routes from '@/../config/env';

export const BET_TICKER_CLIENT_NAME = 'bet-ticker';

export { isAbortError } from '@/utils/abort-error';

const { gql, betTickerGql } = routes;

const httpLink = createHttpLink({
  uri: gql,
});

const betTickerHttpLink = createHttpLink({
  uri: betTickerGql,
});

const buildAuthHeaders = (token, headers = {}) => {
  const { isSuperAdmin, client } = getSuperAdminData();
  const authHeaders = {
    ...headers,
    authorization: token ? `Bearer ${token}` : '',
  };
  if (isSuperAdmin) {
    // For special cases where we want to have manual client.
    // For example allOperators query have '*' value for x-admin-for
    authHeaders['x-admin-for'] = authHeaders['x-admin-for'] || client;
  }
  return authHeaders;
};

const asyncAuthLink = setContext(async (_, { headers }) => {
  try {
    const { token } = await Cognito.getAuthSession();
    return { headers: buildAuthHeaders(token, headers) };
  } catch (error) {
    Cognito.notifySessionInvalid();
    throw error;
  }
});

const isUnauthorizedError = (error) => {
  const networkError = error?.networkError || error?.cause;
  return networkError?.statusCode === 401;
};

const authErrorLink = onError(({ networkError, operation, forward }) => {
  if (!isUnauthorizedError({ networkError })) return undefined;

  const context = operation.getContext();
  if (context.authRetry) {
    Cognito.notifySessionInvalid();
    return undefined;
  }

  return new Observable((observer) => {
    Cognito.getAuthSession({ forceRefresh: true })
      .then(({ token }) => {
        operation.setContext({
          ...context,
          authRetry: true,
          headers: buildAuthHeaders(token, context.headers),
        });
        forward(operation).subscribe(observer);
      })
      .catch((error) => {
        Cognito.notifySessionInvalid();
        observer.error(error);
      });
  });
});

const connections = {};

const cancelRequestLink = new ApolloLink((operation, forward) => {
  const context = operation.getContext();
  const { requestTrackerId } = context;

  if (requestTrackerId && connections[requestTrackerId]) {
    connections[requestTrackerId].abort();
  }

  const controller = requestTrackerId ? new AbortController() : null;

  if (requestTrackerId && controller) {
    connections[requestTrackerId] = controller;
    operation.setContext({
      ...context,
      fetchOptions: {
        ...context?.fetchOptions,
        signal: controller.signal,
      },
    });
  }

  return new Observable((observer) => {
    const subscription = forward(operation).subscribe({
      next: (result) => observer.next(result),
      error: (err) => {
        if (requestTrackerId && connections[requestTrackerId] === controller) {
          delete connections[requestTrackerId];
        }
        if (controller?.signal?.aborted || isAbortError(err)) {
          return;
        }
        observer.error(err);
      },
      complete: () => {
        if (requestTrackerId && connections[requestTrackerId] === controller) {
          delete connections[requestTrackerId];
        }
        observer.complete();
      },
    });

    return () => {
      subscription.unsubscribe();
      if (controller && !controller.signal.aborted) {
        controller.abort();
      }
    };
  });
});

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        eventByEventId: {
          merge: true,
        },
      },
    },
  },
});

const buildLink = (httpLinkTarget) => authErrorLink
  .concat(asyncAuthLink)
  .concat(cancelRequestLink)
  .concat(httpLinkTarget);

export default new ApolloClient({
  link: ApolloLink.split(
    (operation) => operation.getContext().clientName === BET_TICKER_CLIENT_NAME,
    buildLink(betTickerHttpLink),
    buildLink(httpLink),
  ),
  cache,
  queryDeduplication: false,
});
